import { ChoiceList } from "./ChoiceList.js";
import { serviceCategoryLabels, type ClassifyResult, type ServiceCategory } from "../api.js";

/**
 * Screen 2 of the AI intake: confirm where we're routing the request. Confidence
 * is expressed through the UI (a confident "Looks like X" vs. "Which fits best?"),
 * never as a number or any "AI"/model wording. There's always an escape to the
 * full manual list.
 */

// At/above this we present a single confident guess to confirm; below it we ask
// the user to pick among the closest matches. Tuned to gpt-4.1-nano's
// calibration (clear cases score ~0.90–0.95; genuinely ambiguous ones ~0.70),
// so 0.85 confirms the clear cases and shows the picker for the rest.
const HIGH_CONFIDENCE = 0.85;

export function ConfirmRoutingStep({
  result,
  onConfirm,
  onChooseManually,
}: {
  result: ClassifyResult;
  onConfirm: (category: ServiceCategory) => void;
  onChooseManually: () => void;
}) {
  if (result.confidence >= HIGH_CONFIDENCE) {
    const label = serviceCategoryLabels[result.category];
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Looks like {label}.</h2>
          <p className="mt-1 text-sm text-slate-500">Is that the right kind of help?</p>
        </div>
        <button
          type="button"
          onClick={() => onConfirm(result.category)}
          className="min-h-[44px] w-full rounded-xl bg-teal-700 text-base font-semibold text-white shadow-sm active:bg-teal-800"
        >
          Yes, that’s right
        </button>
        <button
          type="button"
          onClick={onChooseManually}
          className="block w-full text-center text-sm font-medium text-teal-700"
        >
          No, pick a different one
        </button>
      </div>
    );
  }

  // Lower confidence → offer the top matches (primary + alternates), de-duped.
  const candidates: ServiceCategory[] = [];
  for (const c of [result.category, ...result.alternates.map((a) => a.category)]) {
    if (!candidates.includes(c)) candidates.push(c);
    if (candidates.length >= 4) break;
  }

  return (
    <div className="space-y-4">
      <ChoiceList
        heading="Which of these fits best?"
        options={candidates.map((c) => ({ value: c, label: serviceCategoryLabels[c] }))}
        selected=""
        onSelect={(value) => onConfirm(value as ServiceCategory)}
      />
      <button
        type="button"
        onClick={onChooseManually}
        className="block w-full text-center text-sm font-medium text-teal-700"
      >
        See all services
      </button>
    </div>
  );
}
